/** @jsxImportSource @emotion/react */
import tw from "twin.macro";
import { css } from "@emotion/css";
import {
  addDays,
  eachDayOfInterval,
  format,
  formatISO,
  isEqual,
  startOfMonth,
  startOfToday,
  startOfTomorrow,
  startOfWeek,
} from "date-fns";
import * as R from "ramda";
import React, {
  forwardRef,
  memo,
  Ref,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SplitPane from "react-split-pane";
import AutoSizer from "react-virtualized-auto-sizer";
import { GridOnScrollProps, VariableSizeGrid as Grid } from "react-window";
import { CalendarBodyColumn } from "./CalendarBodyColumn";
import { scaleTime } from "d3-scale";
import mergeRefs from "react-merge-refs";
import { FiSkipBack, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { useDayHeight, useDayWidth } from "../state/state/viewOptionsStore";

export type DayColumn = {
  type: "DAY";
  d: Date;
};

type WeekColumn = {
  type: "WEEK";
  d: Date;
};

type MonthColumn = {
  type: "MONTH";
  d: Date;
};

export type Column = DayColumn | WeekColumn | MonthColumn;

export const columnToId = (c: Column) => c.type + formatISO(c.d);

export const DAY_BACKLOG_HEIGHT = 360;

const insertWeeks = (days: Column[]) => {
  return days.flatMap((col, i) => {
    if (
      col.type === "DAY" &&
      i !== 0 && // never put a week first
      isEqual(startOfWeek(col.d, { weekStartsOn: 1 }), col.d)
    ) {
      return [{ type: "WEEK", d: col.d }, col];
    } else {
      return col;
    }
  }) as Column[];
};

const insertMonths = (days: Column[]) => {
  return days.flatMap((day, i) => {
    if (day.type === "DAY" && i !== 0 && isEqual(startOfMonth(day.d), day.d)) {
      return [{ type: "MONTH", d: day.d }, day];
    } else {
      return day;
    }
  }) as Column[];
};

const CalendarWeeksGrid = memo(
  forwardRef(
    (
      {
        weekRanges,
        height,
        width,
        onScroll,
      }: {
        weekRanges: WeekRange[];
        height: number;
        width: number;
        onScroll: (props: GridOnScrollProps) => any;
      },
      ref: any
    ) => {
      const dayWidth = useDayWidth();
      const localRef = useRef();
      useLayoutEffect(() => {
        if (!!localRef && !!localRef.current) {
          //@ts-ignore
          localRef.current.resetAfterColumnIndex(0);
        }
      }, [dayWidth]);

      return (
        <Grid
          className={css`
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
            ::-webkit-scrollbar {
              display: none;
            }
          `}
          columnCount={weekRanges.length}
          columnWidth={(index) =>
            (weekRanges[index].end - weekRanges[index].start) * dayWidth
          }
          height={height}
          onScroll={onScroll}
          rowCount={1}
          rowHeight={(index) => height}
          width={width}
          //@ts-ignore
          ref={mergeRefs([ref, localRef])}
        >
          {({ columnIndex, rowIndex, style }) => {
            return (
              <div
                style={{ ...style, display: "flex" }}
                tw="border-gray-200 border-b"
              >
                {/* <span tw="sticky left-2 ml-2 mr-2 my-auto">
                  <FiArrowLeft tw="inline-block" /> Week{" "}
                  {format(weekRanges[columnIndex].d, "w")}{" "}
                  <FiArrowRight tw="inline-block" />
                </span> */}
                <span tw="sticky left-2 ml-2 mr-2 my-auto">
                  Week {format(weekRanges[columnIndex].d, "w")}{" "}
                </span>
              </div>
            );
          }}
        </Grid>
      );
    }
  )
);

const CalendarMonthsGrid = memo(
  forwardRef(
    (
      {
        monthRanges,
        height,
        width,
        onScroll,
      }: {
        monthRanges: WeekRange[];
        height: number;
        width: number;
        onScroll: (props: GridOnScrollProps) => any;
      },
      ref: any
    ) => {
      const dayWidth = useDayWidth();
      const localRef = useRef();
      useLayoutEffect(() => {
        if (!!localRef && !!localRef.current) {
          //@ts-ignore
          localRef.current.resetAfterColumnIndex(0);
        }
      }, [dayWidth]);

      return (
        <Grid
          className={css`
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
            ::-webkit-scrollbar {
              display: none;
            }
          `}
          columnCount={monthRanges.length}
          columnWidth={(index) =>
            (monthRanges[index].end - monthRanges[index].start) * dayWidth
          }
          height={height}
          rowCount={1}
          rowHeight={(index) => height}
          width={width}
          onScroll={onScroll}
          //@ts-ignore
          ref={mergeRefs([localRef, ref])}
        >
          {({ columnIndex, rowIndex, style }) => {
            return (
              <div
                style={{ ...style, display: "flex" }}
                // tw="border-gray-200 border"
              >
                <span tw="sticky left-2 ml-2 mr-2 my-auto">
                  {/* <FiArrowLeft tw="inline-block" />{" "}
                  {format(monthRanges[columnIndex].d, "MMMM yyyy")}{" "}
                  <FiArrowRight tw="inline-block" /> */}{" "}
                  {format(monthRanges[columnIndex].d, "MMMM yyyy")}{" "}
                </span>
              </div>
            );
          }}
        </Grid>
      );
    }
  )
);

const CalendarBodyGrid = forwardRef(
  (
    {
      columns,
      height,
      width,
      onScroll,
    }: {
      columns: Column[];
      height: number;
      width: number;
      onScroll: (props: GridOnScrollProps) => any;
    },
    ref: any
  ) => {
    const dayHeight = useDayHeight();
    const dayWidth = useDayWidth();

    const localRef = useRef();
    useLayoutEffect(() => {
      if (!!localRef && !!localRef.current) {
        //@ts-ignore
        localRef.current.resetAfterColumnIndex(0);
      }
    }, [dayWidth]);

    return (
      <Grid
        className={css``}
        columnCount={columns.length}
        columnWidth={(i) => dayWidth}
        height={height}
        rowCount={1}
        rowHeight={() => dayHeight + 70 + DAY_BACKLOG_HEIGHT}
        width={width}
        onScroll={onScroll}
        //@ts-ignore
        ref={mergeRefs([ref, localRef])}
      >
        {({ columnIndex, rowIndex, style }) => {
          return (
            <div
              style={{
                ...style,
                borderRight: "1px solid rgba(0, 0, 0, 0.202)",
              }}
            >
              <CalendarBodyColumn col={columns[columnIndex]} />
            </div>
          );
        }}
      </Grid>
    );
  }
);

type WeekRange = {
  d: Date;
  start: number;
  end: number;
};

const generateWeekRange = (columns: Column[]) => {
  const colsWithWeek = columns.map((col) => ({
    ...col,
    week: startOfWeek(col.d, { weekStartsOn: 1 }),
  }));
  const rawWeeks = R.uniq(colsWithWeek.map((c) => c.week));

  return rawWeeks.map((w) => ({
    d: w,
    start: R.findIndex(R.propEq("week", w), colsWithWeek) + 1,
    end: R.findLastIndex(R.propEq("week", w), colsWithWeek) + 2,
  }));
};

type MonthRange = {
  d: Date;
  start: number;
  end: number;
};

const generateMonthRange = (columns: Column[]) => {
  const colsWithMonth = columns.map((col) => ({
    ...col,
    month: startOfMonth(col.d),
  }));
  const rawMonths = R.uniq(colsWithMonth.map((c) => c.month));

  return rawMonths.map((w) => ({
    d: w,
    start: R.findIndex(R.propEq("month", w), colsWithMonth) + 1,
    end: R.findLastIndex(R.propEq("month", w), colsWithMonth) + 2,
  }));
};
export const Calendar = () => {
  const dayHeight = useDayHeight();
  console.log("render of calendar w/", dayHeight);

  const [interval, setInterval] = useState(() => ({
    start: addDays(new Date(), 0),
    end: addDays(new Date(), 364),
  }));

  const columns: Column[] = useMemo(() => {
    console.count("generating columns");
    return insertWeeks(
      insertMonths(
        eachDayOfInterval(interval).map((d) => ({
          type: "DAY",
          d,
        }))
      )
    );
  }, [interval]);

  // TODO: Weeks/month colunms should be able to be different width than day columns indexToColumnWidth

  const weekRanges: WeekRange[] = useMemo(
    () => generateWeekRange(columns),
    [columns]
  );
  const monthRanges: MonthRange[] = useMemo(
    () => generateMonthRange(columns),
    [columns]
  );

  const bodyRef = React.createRef<Grid>();
  const weeksRef = React.createRef<Grid>();
  const monthsRef = React.createRef<Grid>();
  const onScroll = ({ scrollLeft, scrollUpdateWasRequested }) => {
    window.requestAnimationFrame(() => {
      // if (!scrollUpdateWasRequested && !!bodyRef.current) {
      //   //@ts-ignore
      //   bodyRef.current.scrollTo({ scrollLeft });
      // }
      if (!scrollUpdateWasRequested && !!weeksRef.current) {
        weeksRef.current.scrollTo({ scrollLeft, scrollTop: 0 });
      }
      if (!scrollUpdateWasRequested && !!monthsRef.current) {
        monthsRef.current.scrollTo({ scrollLeft, scrollTop: 0 });
      }
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const blah = useCallback(
    // TODO: is called too early, dayHeight has initial not customized value.
    R.once((ref: Grid) => {
      // Scroll to right now
      const scale = scaleTime()
        .domain([startOfToday(), startOfTomorrow()])
        .range([0, dayHeight]);
      if (ref !== null) {
        //@ts-ignore
        ref.scrollTo({ scrollTop: scale(new Date()) - 200 });
      }
    }),
    [dayHeight]
  );

  return (
    <div tw="flex flex-col h-full">
      <div
        className={css`
          height: 64px;
          width: 100%;
        `}
        tw="flex"
      >
        <div
          tw="absolute text-black left-2 cursor-pointer z-30 flex"
          style={{ top: "64px", height: "60px" }}
          title="Go back in time"
          onClick={() => {
            //@ts-ignore
            const currentScroll: number = bodyRef.current?.state.scrollLeft;
            if (currentScroll === 0) {
              setInterval((interval) => ({
                start: addDays(interval.start, -7),
                end: addDays(interval.end, -7),
              }));
            } else {
              onScroll({ scrollLeft: 0, scrollUpdateWasRequested: false });
            }
          }}
        >
          <FiSkipBack tw="text-xl m-auto"></FiSkipBack>
        </div>
        <div tw="flex flex-grow flex-col h-full">
          <div tw="h-8">
            <AutoSizer>
              {({ height, width }) => (
                <CalendarMonthsGrid
                  monthRanges={monthRanges}
                  height={height}
                  width={width}
                  ref={monthsRef}
                  onScroll={() => {}}
                />
              )}
            </AutoSizer>
          </div>
          <div tw="h-8">
            <AutoSizer>
              {({ height, width }) => (
                <CalendarWeeksGrid
                  weekRanges={weekRanges}
                  height={height}
                  width={width}
                  ref={weeksRef}
                  onScroll={() => {}}
                />
              )}
            </AutoSizer>
          </div>
        </div>
      </div>
      <div tw="flex-1">
        <AutoSizer>
          {({ height, width }) => (
            <CalendarBodyGrid
              columns={columns}
              height={height}
              width={width}
              onScroll={onScroll}
              ref={mergeRefs([bodyRef, blah])}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
};
